/**
 * Every tunable duration the SPA holds, in one place.
 *
 * They were a file apart and kept being mistaken for each other, because they are both "a few
 * hundred-to-thousand milliseconds about saving and loading". They are not the same kind of number,
 * and the difference is the point:
 *
 *                    LOADER_DELAY                  DRAFT_SAVE_DEBOUNCE_MS
 *   controls         how long a loading state      how long to wait before a network
 *                    stays VISIBLE                 WRITE
 *   exists to stop   a fast backend flickering     a burst of edits hammering the server
 *   raise it and     the UI feels sluggish         more unsaved work is at risk
 *   lower it and     the flicker comes back        more requests
 *   authority        HERE — client-only            config/drafts.php; these are FALLBACKS
 *                                                  until GET /api/config answers
 *
 * That last row matters most: changing a DRAFT_SAVE_* value here does NOT change production
 * behaviour, because the server's value wins as soon as it arrives. Change those in
 * `api/config/drafts.php` (or the matching DRAFT_* env var). LOADER_DELAY has no server half, so
 * this file is the only place it lives.
 */

export type DelayRange = {
  minMs: number;
  maxMs: number;
};

/**
 * How long a loading state stays up once it has appeared.
 *
 * The point is NOT to look busy — it is that a backend answering in 20ms produces a one-frame flash
 * that reads as a glitch rather than as progress. Holding the state for a beat makes the wait
 * legible. Randomized so repeated actions do not feel mechanical; matches the upload transition this
 * was first tuned against.
 */
export const LOADER_DELAY: DelayRange = {
  minMs: 300,
  maxMs: 700
};

/**
 * Idle time before in-progress editor work is auto-saved as a draft.
 *
 * FALLBACK ONLY — `drafts.autosave_debounce_ms` is the real setting. No rationale for 4200 is
 * recorded anywhere: the commit that introduced it (eb9d5cf) describes "3s -> 5s" in its own
 * message while shipping this number, so the two disagree and neither explains itself.
 */
export const DRAFT_SAVE_DEBOUNCE_MS = 4200;

/**
 * CEILING on the debounce, and the reason auto-save works for someone who does not stop.
 *
 * The debounce waits for a PAUSE and every edit restarts it, so steady work never reaches it:
 * measured at ten edits over thirty-one seconds with zero saves. This forces one once the oldest
 * unsaved change is this old. Zero turns it off.
 *
 * FALLBACK ONLY — `drafts.autosave_max_wait_ms` is the real setting.
 */
export const DRAFT_SAVE_MAX_WAIT_MS = 30000;

/** FALLBACK ONLY — `drafts.save_timeout_ms`. One save attempt is abandoned after this long. */
export const DRAFT_SAVE_TIMEOUT_MS = 20000;

/** FALLBACK ONLY — `drafts.retry_delays_ms`. Waits between automatic retries, in order. */
export const DRAFT_SAVE_RETRY_DELAYS_MS: ReadonlyArray<number> = [10000, 30000, 60000];
